import { useState, useEffect, useRef, useCallback } from 'react';

interface UseCountUpOptions {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  startOnView?: boolean;
  threshold?: number;
}

interface UseCountUpReturn {
  displayValue: string;
  elementRef: React.RefObject<HTMLDivElement | null>;
  reset: () => void;
}

export default function useCountUp({
  end,
  duration = 2000,
  suffix = '',
  prefix = '',
  decimals = 0,
  startOnView = true,
  threshold = 0.2,
}: UseCountUpOptions): UseCountUpReturn {
  const [displayValue, setDisplayValue] = useState('0');
  const elementRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const hasStartedRef = useRef(false);

  const animate = useCallback(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic for a smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;

      setDisplayValue(current.toFixed(decimals));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplayValue(end.toFixed(decimals));
      }
    };

    rafRef.current = requestAnimationFrame(step);
  }, [end, duration, decimals]);

  // Force start without waiting for viewport
  useEffect(() => {
    if (!startOnView) {
      animate();
    }
    // Only run on mount when startOnView is false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intersection Observer for viewport-triggered animation
  useEffect(() => {
    if (!startOnView) return;

    const node = elementRef.current;
    if (!node) return;

    // Use a smaller rootMargin to trigger slightly before the element is fully visible
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate();
          // Once triggered, disconnect to avoid re-triggering
          observer.disconnect();
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
    // animate is stable (only changes with end/duration/decimals)
  }, [startOnView, animate, threshold]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const reset = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    hasStartedRef.current = false;
    setDisplayValue('0');
  }, []);

  return {
    displayValue: `${prefix}${displayValue}${suffix}`,
    elementRef,
    reset,
  };
}